/**
 * useGlobalShortcuts — App-level keyboard navigation (Gmail/GitHub-style).
 *
 * Provides G-sequence navigation (G then D = Dashboard, G then I = Invoices),
 * help modal toggle (?), and search focus (/).
 *
 * Suppressed when: a modal is open, user is typing in an input, or POS view is active.
 *
 * Usage in App.vue:
 *   const { showHelp, pendingG } = useGlobalShortcuts(router, routes)
 */

import { ref, onMounted, onUnmounted } from 'vue'

/**
 * @param {import('vue-router').Router} router - Vue Router instance
 * @param {Object} options
 * @param {Object<string, string>} options.routes - Map of key -> route path (e.g., { d: '/', i: '/invoices' })
 * @param {Function} [options.onHelp] - Called when ? is pressed (toggle help modal)
 */
export function useGlobalShortcuts(router, options = {}) {
  const { routes = {}, onHelp } = options

  const pendingG = ref(false) // True when G was pressed, waiting for second key
  const showHelp = ref(false) // Help modal visibility

  // Elements that should suppress shortcuts
  const INPUT_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT'])

  const isInInput = () => {
    const el = document.activeElement
    if (!el) return false
    if (INPUT_TAGS.has(el.tagName)) return true
    if (el.isContentEditable) return true
    return false
  }

  const isModalOpen = () => {
    return !!document.querySelector('.intercom-modal, .intercom-modal-overlay')
  }

  const isPOSActive = () => {
    return !!document.querySelector('.pos-view')
  }

  const cancelG = () => {
    pendingG.value = false
  }

  const handleKeydown = (e) => {
    // Never interfere with POS (it has its own comprehensive keyboard system)
    if (isPOSActive()) return

    // Never interfere when a modal is open (forms handle their own keys)
    if (isModalOpen()) return

    // Never interfere with modifier combos (Ctrl, Cmd, Alt)
    if (e.ctrlKey || e.metaKey || e.altKey) return

    const key = e.key.toLowerCase()

    // --- G-sequence handling ---
    if (pendingG.value) {
      cancelG()

      // Check if the second key matches a route
      const routePath = routes[key]
      if (routePath) {
        e.preventDefault()
        router.push(routePath)
      }
      return
    }

    // Don't process single-key shortcuts when typing
    if (isInInput()) return

    // --- Single-key shortcuts ---

    // Escape: cancel G-sequence if pending, or blur active element
    if (key === 'escape') {
      if (pendingG.value) {
        cancelG()
        e.preventDefault()
        return
      }
      if (document.activeElement && document.activeElement !== document.body) {
        document.activeElement.blur()
      }
      return
    }

    // Don't process other single-key shortcuts when typing
    if (isInInput()) return

    // G: start navigation sequence (stays open until Escape or key press)
    if (key === 'g') {
      pendingG.value = true
      return
    }

    // ?: toggle help modal
    if (key === '?' || (e.shiftKey && key === '/')) {
      e.preventDefault()
      if (onHelp) {
        onHelp()
      } else {
        showHelp.value = !showHelp.value
      }
      return
    }

    // /: focus search input on the current page
    if (key === '/') {
      const searchInput = document.querySelector(
        '.search-input input, .intercom-input-container input[type="text"], input[placeholder*="Search"], input[placeholder*="search"]'
      )
      if (searchInput) {
        e.preventDefault()
        searchInput.focus()
        searchInput.select()
      }
      return
    }

  }

  onMounted(() => {
    document.addEventListener('keydown', handleKeydown)
  })

  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown)
    cancelG()
  })

  return {
    pendingG,
    showHelp,
    cancelG,
  }
}
